import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { fetchCurrentUser, getStoredUser, logout } from "../../api/client";
import "./app-layout.scss";

const menuItems = [
  { label: "Dashboard", icon: "ph:squares-four-bold", path: "/admin" },
  { label: "Products", icon: "ph:package-bold", path: "/admin/products" },
  { label: "Enquiry", icon: "ph:envelope-simple-bold", path: "/admin/enquiries" },
  { label: "Articles", icon: "ph:article-bold", path: "/admin/articles" },
  { label: "Users", icon: "ph:users-bold", path: "/admin/users" },
  { label: "Settings", icon: "ph:gear-bold", path: "/admin/settings" },
  { label: "Audit Trail", icon: "ph:list-checks-bold", path: "/admin/audit-trail" },
];

const pageTitles = {
  "/admin": ["Overview", "Dashboard"],
  "/admin/products": ["Storefront Management", "Products"],
  "/admin/products/create": ["Storefront Management", "New Product"],
  "/admin/products/categories": ["Storefront Management", "Product Categories"],
  "/admin/products/categories/create": ["Storefront Management", "New Category"],
  "/admin/enquiries": ["Storefront Management", "Enquiry"],
  "/admin/articles": ["Content Management", "Articles"],
  "/admin/articles/create": ["Content Management", "Create Article"],
  "/admin/articles/media": ["Content Management", "Media Library"],
  "/admin/articles/categories": ["Content Management", "Categories"],
  "/admin/articles/categories/create": ["Content Management", "New Category"],
  "/admin/users": ["Administration", "Users"],
  "/admin/settings": ["Administration", "Website Settings"],
  "/admin/audit-trail": ["Administration", "Audit Trail"],
};

const resolvePageTitle = (pathname) => {
  if (pathname.startsWith("/admin/products/")) {
    if (pathname === "/admin/products/create") {
      return ["Storefront Management", "New Product"];
    }
    if (pathname === "/admin/products/categories") {
      return ["Storefront Management", "Product Categories"];
    }
    if (pathname === "/admin/products/categories/create") {
      return ["Storefront Management", "New Category"];
    }
    if (/^\/admin\/products\/categories\/\d+\/edit$/.test(pathname)) {
      return ["Storefront Management", "Edit Category"];
    }
    return ["Storefront Management", "Edit Product Details"];
  }
  if (/^\/admin\/enquiries\/\d+$/.test(pathname)) {
    return ["Storefront Management", "Enquiry Details"];
  }
  if (/^\/admin\/articles\/\d+\/edit$/.test(pathname)) {
    return ["Content Management", "Edit Article"];
  }
  if (/^\/admin\/articles\/\d+\/preview$/.test(pathname)) {
    return ["Content Management", "Article Preview"];
  }
  if (/^\/admin\/articles\/categories\/\d+\/edit$/.test(pathname)) {
    return ["Content Management", "Edit Category"];
  }
  return pageTitles[pathname] || pageTitles["/admin"];
};

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(getStoredUser());
  const [sessionWarning, setSessionWarning] = useState("");
  const sessionChecked = useRef(false);
  const [eyebrow, title] = resolvePageTitle(location.pathname);

  useEffect(() => {
    if (sessionChecked.current) return;
    sessionChecked.current = true;

    fetchCurrentUser()
      .then((currentUser) => {
        setUser(currentUser);
        setSessionWarning("");
      })
      .catch((error) => {
        if (error.status === 401) {
          logout().finally(() => navigate("/login", { replace: true }));
          return;
        }

        setSessionWarning("The server is temporarily unavailable. Your login has been kept; please try again shortly.");
      });
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.name || "Admin")
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="app-layout">
      <aside className={`app-layout__sidebar ${sidebarOpen ? "app-layout__sidebar--open" : ""}`}>
        <div className="app-layout__brand">
          <div className="app-layout__wordmark">
            <span>興和利貿易有限公司</span>
            <strong>Hin Ho Lee Trading Sdn. Bhd.</strong>
          </div>
          <span>ADMIN PORTAL</span>
        </div>

        <nav className="app-layout__menu">
          <p className="app-layout__nav-label">MANAGEMENT</p>
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/admin"}
              className={({ isActive }) =>
                `app-layout__menu-item ${isActive ? "app-layout__menu-item--active" : ""}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <span className="app-layout__menu-icon">
                <Icon icon={item.icon} height={14} />
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <button className="app-layout__logout" type="button" onClick={handleLogout}>Log out</button>
      </aside>

      {sidebarOpen && <button className="app-layout__overlay" type="button" aria-label="Close menu" onClick={() => setSidebarOpen(false)} />}

      <main className="app-layout__main">
        {sessionWarning && (
          <div className="app-layout__session-warning">
            {sessionWarning}
            <button type="button" onClick={() => setSessionWarning("")} aria-label="Dismiss warning">
              <Icon icon="ph:x" height={16} />
            </button>
          </div>
        )}
        <header className="app-layout__topbar">
          <button className="app-layout__menu-toggle" type="button" aria-label="Open menu" onClick={() => setSidebarOpen(true)}>
            <Icon icon="ph:list" height={22} />
          </button>
          <div className="app-layout__heading">
            <p className="app-layout__eyebrow">{eyebrow}</p>
            <h1 className="app-layout__title">{title}</h1>
          </div>
          <div className="app-layout__profile"><span className="app-layout__avatar">{initials}</span><span><strong>{user?.name || "Administrator"}</strong><small>{user?.job_title || "Administrator"}</small></span></div>
        </header>
        <section className="app-layout__content"><Outlet /></section>
      </main>
    </div>
  );
};

export default AppLayout;
